/**
 * Servicio para manejar las notificaciones toast
 * Actúa como intermediario entre los componentes y el componente toast-notifications
 */

// Tipado para el componente toast
interface ToastComponent {
  show(message: string, type?: string, duration?: number): number;
  success(message: string, duration?: number): number;
  error(message: string, duration?: number): number;
  warning(message: string, duration?: number): number;
  info(message: string, duration?: number): number;
  hide(toastId: number): void;
}

// Tipado para el elemento toast creado
interface ToastElement extends HTMLElement {
  _tag?: ToastComponent;
}

// Tipado para los colores/tipos de toast
type ToastType = 'primary' | 'success' | 'danger' | 'error' | 'warning' | 'info' | 'secondary';

// Instancia del componente toast
let toastComponent: ToastComponent | null = null;

// Método para establecer la referencia al componente de toast
const setToastComponent = (component: ToastComponent): void => {
  toastComponent = component;

  // También registramos una referencia global como fallback
  if (typeof window !== 'undefined') {
    (window as any)._toastComponent = component;
    console.log('ToastService: Component reference stored successfully');
  }
};

// Método para obtener el componente de toast (prioriza la instancia local, pero busca en window como fallback)
const getToastComponent = (): ToastComponent | null => {
  // Si ya tenemos una referencia, usarla
  if (toastComponent) {
    return toastComponent;
  }

  // Si no tenemos referencia, intentar conseguirla
  if (typeof window !== 'undefined') {
    // Intentar obtenerla del registro global
    if ((window as any)._toastComponent) {
      return (window as any)._toastComponent as ToastComponent;
    }

    // Intentar obtenerla de la instancia global
    if ((window as any)._toastComponentInstance) {
      return (window as any)._toastComponentInstance as ToastComponent;
    }

    // Intentar obtenerla del DOM
    const toastElement = document.getElementById('globalToastComponent') as ToastElement;
    if (toastElement && toastElement._tag) {
      // Guardar para uso futuro
      setToastComponent(toastElement._tag);
      return toastElement._tag;
    } else if (toastElement) {
      // Si tenemos el elemento pero no el _tag, esperar un poco
      setTimeout(() => {
        if (toastElement._tag) {
          setToastComponent(toastElement._tag);
        }
      }, 500);
    }
  }

  return null;
};

// Método independiente para mostrar toasts directamente usando DOM
const createToastDirectly = (message: string, type?: ToastType, duration: number = 3000): HTMLElement => {
  // Verificar si existe el contenedor
  let container = document.getElementById('toast-container');

  // Si no existe, crearlo
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.className = 'position-fixed bottom-0 end-0 p-3';
    container.style.zIndex = '1100';
    document.body.appendChild(container);
  }

  // Determinar icono y título para el toast
  let icon = 'info-circle-fill';
  let title = 'Información';
  let bsType = type || 'primary';

  switch (bsType) {
    case 'success':
      icon = 'check-circle-fill';
      title = 'Éxito';
      break;
    case 'danger':
    case 'error':
      icon = 'exclamation-triangle-fill';
      title = 'Error';
      bsType = 'danger'; // Normalizar 'error' a 'danger'
      break;
    case 'warning':
      icon = 'exclamation-triangle-fill';
      title = 'Advertencia';
      break;
  }

  // Crear elemento toast
  const toastElement = document.createElement('div');
  toastElement.className = 'toast show mb-2';
  toastElement.setAttribute('role', 'alert');
  toastElement.setAttribute('aria-live', 'assertive');
  toastElement.setAttribute('aria-atomic', 'true');

  // Definir el contenido HTML del toast
  toastElement.innerHTML = `
    <div class="toast-header">
      <i class="bi bi-${icon} text-${bsType} me-2"></i>
      <strong class="me-auto">${title}</strong>
      <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
    </div>
    <div class="toast-body">
      ${message}
    </div>
  `;

  // Añadir al contenedor
  container.appendChild(toastElement);

  // Configurar cierre manual
  const closeButton = toastElement.querySelector('.btn-close');
  if (closeButton) {
    closeButton.addEventListener('click', () => {
      if (toastElement.parentNode) {
        toastElement.classList.remove('show');
        setTimeout(() => {
          if (toastElement.parentNode) {
            toastElement.parentNode.removeChild(toastElement);
          }
        }, 150);
      }
    });
  }

  // Configurar autocierre
  setTimeout(() => {
    if (toastElement.parentNode) {
      toastElement.classList.remove('show');
      setTimeout(() => {
        if (toastElement.parentNode) {
          toastElement.parentNode.removeChild(toastElement);
        }
      }, 150);
    }
  }, duration);

  return toastElement;
};

// Métodos para mostrar diferentes tipos de toast
const showSuccess = (message: string, duration: number = 3000): number => {
  const component = getToastComponent();
  if (component) {
    return component.success(message, duration);
  } else {
    console.warn('Toast component is not initialized, using fallback method');
    // Usar el método directo como fallback
    createToastDirectly(message, 'success', duration);
    return -1;
  }
};

const showError = (message: string, duration: number = 3000): number => {
  const component = getToastComponent();
  if (component) {
    return component.error(message, duration);
  } else {
    console.warn('Toast component is not initialized, using fallback method');
    // Usar el método directo como fallback
    createToastDirectly(message, 'danger', duration);
    return -1;
  }
};

const showWarning = (message: string, duration: number = 3000): number => {
  const component = getToastComponent();
  if (component) {
    return component.warning(message, duration);
  } else {
    console.warn('Toast component is not initialized, using fallback method');
    // Usar el método directo como fallback
    createToastDirectly(message, 'warning', duration);
    return -1;
  }
};

const showInfo = (message: string, duration: number = 3000): number => {
  const component = getToastComponent();
  if (component) {
    return component.info(message, duration);
  } else {
    console.warn('Toast component is not initialized, using fallback method');
    // Usar el método directo como fallback
    createToastDirectly(message, 'info', duration);
    return -1;
  }
};

// Método general para mostrar cualquier tipo de toast
const show = (message: string, type: ToastType = 'primary', duration: number = 3000): number => {
  const component = getToastComponent();
  if (component) {
    return component.show(message, type, duration);
  } else {
    console.warn('Toast component is not initialized, using fallback method');
    // Usar el método directo como fallback
    createToastDirectly(message, type, duration);
    return -1;
  }
};

// Método para ocultar un toast específico
const hide = (toastId: number): void => {
  const component = getToastComponent();
  if (component) {
    component.hide(toastId);
  }
};

// Interfaz del servicio toast
export interface IToastService {
  setToastComponent: (component: ToastComponent) => void;
  getToastComponent: () => ToastComponent | null;
  show: (message: string, type?: ToastType, duration?: number) => number;
  success: (message: string, duration?: number) => number;
  error: (message: string, duration?: number) => number;
  warning: (message: string, duration?: number) => number;
  info: (message: string, duration?: number) => number;
  hide: (toastId: number) => void;
}

// Exportar los métodos del servicio
export const ToastService: IToastService = {
  setToastComponent,
  getToastComponent,
  show,
  success: showSuccess,
  error: showError,
  warning: showWarning,
  info: showInfo,
  hide
};

export default ToastService;
